import React from 'react';
import { Box, Heading, Text, VStack, Image, Flex, Link, IconButton, useColorModeValue } from '@chakra-ui/react';
import { FaYoutube, FaSpotify, FaApple } from 'react-icons/fa6';

const Hero = ({ artistName, tagline, logoUrl, socialLinks }) => {
  const accentColor = useColorModeValue('brand.accent', 'brand.accent'); // Use accent for primary social icons
  const secondaryAccentColor = useColorModeValue('brand.secondaryAccent', 'brand.secondaryAccent'); // Use secondary accent for hover

  // Determine appropriate icon for each social link dynamically
  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'youtube': return <FaYoutube />;
      case 'spotify': return <FaSpotify />;
    //   case 'instagram': return <FaInstagram />;
    //   case 'twitter': return <FaXTwitter />; // For "X" (formerly Twitter)
    //   case 'tiktok': return <FaTiktok />;
      case 'applemusic': return <FaApple />; // For Apple Music links if listed under social
      default: return null;
    }
  };

  return (
    <VStack
      spacing={{ base: 6, md: 8 }} // Vertical spacing between elements
      align="center" // Center align content horizontally
      textAlign="center" // Center align text
      py={{ base: 16, md: 24 }} // Vertical padding
      px={4} // Horizontal padding
      bg="brand.800" // Background color from theme
      borderRadius="3xl" // More rounded corners for the hero section box
      boxShadow="dark-lg" // Dark shadow for depth
      maxW="container.xl" // Max width to keep content readable
      mx="auto" // Center the box itself
      mt={{ base: 4, md: 8 }} // Margin top to push below navbar
    >
      {/* Artist Logo */}
      <Box
        boxSize={{ base: "120px", md: "180px" }} // Responsive size for logo
        borderRadius="full" // Circular logo
        overflow="hidden" // Ensures image stays within bounds
        boxShadow="xl" // Shadow around the logo
        border="4px solid"
        borderColor="brand.accent" // Accent border around logo
      >
        <Image src={logoUrl} alt={`${artistName} Logo`} objectFit="cover" w="100%" h="100%" />
      </Box>

      {/* Artist Name */}
      <Heading
        as="h1"
        size={{ base: "2xl", md: "4xl", lg: "5xl" }}
        fontWeight="extrabold"
        letterSpacing="tight"
        color="brand.300" // Light text color from theme
      >
        {artistName}
      </Heading>

      {/* Tagline */}
      <Text
        fontSize={{ base: "md", md: "lg", lg: "xl" }}
        maxW="2xl" // Max width for tagline for readability
        color="brand.500" // Slightly darker text for tagline
        lineHeight="tall"
      >
        {tagline}
      </Text>

      {/* Social Media Links */}
      <Flex mt={8} wrap="wrap" justifyContent="center" gap={{ base: 3, md: 5 }}>
        {socialLinks && Object.entries(socialLinks).map(([platform, url]) => (
          getSocialIcon(platform) && url && ( // Render only if icon and URL exist
            <IconButton
              key={platform}
              as={Link} // Use Link component for external navigation
              href={url}
              isExternal // Opens link in a new tab
              aria-label={`Link to ${artistName}'s ${platform}`}
              icon={getSocialIcon(platform)}
              size={{ base: "lg", md: "xl" }}
              variant="solid"
              bg={accentColor}
              color="white"
              borderRadius="full"
              _hover={{
                bg: secondaryAccentColor, // Change color on hover
                transform: 'scale(1.1)', // Grow on hover
                transition: 'transform 0.2s ease-in-out',
              }}
              boxShadow="md"
            />
          )
        ))}
      </Flex>
    </VStack>
  );
};

export default Hero;